import React, { useState, useEffect } from 'react';

// Parser function (can be improved for robustness)
function parsePipeDelimitedTable(rawText) {
  if (!rawText || typeof rawText !== 'string') return { headers: [], rows: [], summary: '' };

  const lines = rawText.trim().split('\n');
  let headers = [];
  const rows = [];
  let summaryLines = [];
  let headerProcessed = false;
  let potentialHeaders = [];
  let dividerEncountered = false;

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine || trimmedLine.startsWith('##')) continue; 

    if (trimmedLine.includes('---') && trimmedLine.includes('|')) {
      if (potentialHeaders.length > 0 && headers.length === 0) {
        headers = potentialHeaders;
        headerProcessed = true;
      }
      dividerEncountered = true;
      continue; 
    }
    
    if (trimmedLine.includes('|')) {
        let cells = trimmedLine.split('|').map(cell => cell.trim());
        if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
            cells = cells.slice(1, -1);
        } else if (trimmedLine.startsWith('|')) {
            cells = cells.slice(1);
        } else if (trimmedLine.endsWith('|')) {
            cells = cells.slice(0, -1);
        }
        
        if (cells.length === 0 || cells.every(c => c === '')) continue;

        if (!headerProcessed && !dividerEncountered) {
            if (headers.length === 0) {
                if (cells.every(c => c.length > 0)) { // Heuristic: if all cells have content, it's a potential header
                    potentialHeaders = cells; 
                } else if (potentialHeaders.length > 0) { 
                    headers = potentialHeaders;
                    headerProcessed = true;
                    rows.push(cells); 
                } else {
                    rows.push(cells); // Likely data if no strong header candidate yet
                }
            }
        } else if (headerProcessed || dividerEncountered) {
            // If headers are set, or a divider was seen, this is a data row
            rows.push(cells);
        } else if (potentialHeaders.length > 0 && headers.length === 0) {
             // Edge case: data row encountered before explicit divider but after a potential header line
            headers = potentialHeaders; // Solidify potential header
            headerProcessed = true;
            rows.push(cells);
        }

    } else if (headerProcessed || (rows.length > 0 && dividerEncountered)) { 
        // Non-pipe delimited line after header/data and divider = summary text
        summaryLines.push(trimmedLine);
    } else if (potentialHeaders.length > 0 && !dividerEncountered && headers.length === 0) {
        // Text after a potential header line, but not a divider and not pipe-delimited.
        // Assume potential header was the actual header, and this is summary.
        headers = potentialHeaders;
        headerProcessed = true;
        summaryLines.push(trimmedLine);
    }
  }
  
  if (headers.length === 0 && potentialHeaders.length > 0) {
      headers = potentialHeaders;
  }
  
  let finalRows = [];
  if (headers.length > 0) {
    finalRows = rows.map(row => {
        const newRow = [...row];
        while (newRow.length < headers.length) newRow.push('');
        return newRow.length > headers.length ? newRow.slice(0, headers.length) : newRow;
    }).filter(row => row.length > 0 && !row.every(cell => cell === ''));
  }

  return { headers, rows: finalRows, summary: summaryLines.join('\n') };
}


function ParsedTable({ rawText }) {
  const [tableData, setTableData] = useState({ headers: [], rows: [], summary: '' });

  useEffect(() => {
    if (rawText) {
      setTableData(parsePipeDelimitedTable(rawText));
    }
  }, [rawText]);

  // If no headers, render raw text as it might not be a table or parsing failed for headers.
  if (tableData.headers.length === 0) {
    return <div className="message-content-actual">{rawText}</div>;
  }

  return (
    <div className="parsed-table-container">
      <table>
        <thead>
          <tr>
            {tableData.headers.map((header, index) => (
              <th key={`header-${index}`}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.rows.map((row, rowIndex) => (
            <tr key={`row-${rowIndex}`}>
              {row.map((cell, cellIndex) => (
                <td key={`cell-${rowIndex}-${cellIndex}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {tableData.summary && (
        <div className="table-summary-text">{tableData.summary}</div>
      )}
    </div>
  );
}

export default ParsedTable; 